import AsrsServersPage from 'pages/asrs/AsrsServers';
import AsrsServerPage from 'pages/asrs/AsrsServer';
import { AppProvider, DashboardLayout, PageContainer } from '@toolpad/core';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import ReceptionProcessesPage from 'pages/reception/ReceptionProcesses';
import ProductTraceabilityPage from 'pages/catalog/ProductTraceability';
import ReceptionProcessPage from 'pages/reception/ReceptionProcess';
import PurchaseOrdersPage from './pages/reception/PurchaseOrders';
import PurchaseOrderPage from 'pages/reception/PurchaseOrder';
import ProductsPage from 'pages/catalog/Products';
import CustomersPage from 'pages/picking/Customers';
import SalesOrderPage from 'pages/picking/SalesOrder';
import ProductPage from 'pages/catalog/Product';
import { LoginPage } from './pages/authentication/Login';
import SuppliersPage from 'pages/reception/Suppliers';
import SalesOrdersPage from 'pages/picking/SalesOrders';
import PickingProcessesPage from 'pages/picking/PickingProcesses';
import PickingProcessPage from 'pages/picking/PickingProcess';
import { useAuth } from 'hooks/use-auth';
import { navigation } from './navigation';
import { useEffect } from 'react';
import { theme } from './theme';
import ReplenishmentThresholdsPage from 'pages/replenishment/ReplenishmentThresholds';
import ReplenishmentOrdersPage from 'pages/replenishment/ReplenishmentOrders';
import LocationsPage from 'pages/warehouse/Locations';
import AreasPage from 'pages/warehouse/Areas';
import { Box, Button, Typography } from '@mui/material';
import { useUserInfo } from 'hooks/use-user-info';
import logo from 'images/magikevolution_logo.png';
import StoredItemsPage from 'pages/warehouse/StoredItems';
import TeamsPage from 'pages/management/Teams';
import DevicesPage from 'pages/management/Devices';
import WorkersPage from 'pages/management/Workers';
import TeamPage from 'pages/management/Team';
import CircuitsPage from 'pages/picking/Circuits';
import CircuitPage from 'pages/picking/Circuit';

function ToolbarAccount() {
  const [userInfo, setUserInfo] = useUserInfo();
  const auth = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    setUserInfo(null);
    auth.logout?.();
    navigate('/login');
  };

  return (
    userInfo && (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Typography variant="body2" color="textSecondary">
          {userInfo.userName} ({userInfo.username})
        </Typography>
        <Button color="primary" onClick={handleLogout}>
          Logout
        </Button>
      </Box>
    )
  );
}

export default function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();

  useEffect(() => {
    if (!auth.isAuthenticated && location.pathname !== '/login') {
      navigate('/login', { state: { from: location.pathname } });
    }
  }, [auth.isAuthenticated, location, navigate]);

  if (location.pathname === '/login') {
    return (
      <AppProvider navigation={navigation} theme={theme}>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
        </Routes>
      </AppProvider>
    );
  }

  return (
    <AppProvider navigation={navigation} theme={theme}>
      <DashboardLayout
        sx={{ padding: 0 }}
        branding={{
          logo: <img src={logo} alt="Cloudsales WMS" style={{ width: 44, height: 44 }} />,
          title: 'Cloudsales WMS',
          homeUrl: '/',
        }}
        slots={{
          toolbarAccount: ToolbarAccount,
        }}
      >
        <PageContainer maxWidth={false}>
          <Routes>
            {/* Reception */}
            <Route path="/reception/suppliers" element={<SuppliersPage />} />
            <Route path="/reception/purchase-orders" element={<PurchaseOrdersPage />} />
            <Route path="/reception/purchase-orders/:orderId" element={<PurchaseOrderPage />} />
            <Route path="/reception/reception-processes" element={<ReceptionProcessesPage />} />
            <Route path="/reception/reception-processes/:receptionId" element={<ReceptionProcessPage />} />

            {/* Shipping */}
            <Route path="/shipping/customers" element={<CustomersPage />} />
            <Route path="/shipping/sales-orders" element={<SalesOrdersPage />} />
            <Route path="/shipping/sales-orders/:orderId" element={<SalesOrderPage />} />
            <Route path="/shipping/picking-processes" element={<PickingProcessesPage />} />
            <Route path="/shipping/picking-processes/:pickingId" element={<PickingProcessPage />} />
            <Route path="/shipping/circuits" element={<CircuitsPage />} />
            <Route path="/shipping/circuits/:circuitId" element={<CircuitPage />} />

            {/* Replenishment */}
            <Route path="/replenishment/orders" element={<ReplenishmentOrdersPage />} />
            <Route path="/replenishment/thresholds" element={<ReplenishmentThresholdsPage />} />

            {/* Products */}
            <Route path="/products/items" element={<StoredItemsPage />} />
            <Route path="/products/products" element={<ProductsPage />} />
            <Route path="/products/products/:productId" element={<ProductPage />} />
            <Route path="/products/products/:productId/trace/:batchOrSerialNumber" element={<ProductTraceabilityPage />} />

            {/* Management */}
            <Route path="/management/teams" element={<TeamsPage />} />
            <Route path="/management/teams/:teamId" element={<TeamPage />} />
            <Route path="/management/devices" element={<DevicesPage />} />
            <Route path="/management/workers" element={<WorkersPage />} />

            {/* Warehouse */}
            {/* <Route path="/warehouse/warehouses" element={<WarehousesPage />} /> */}
            {/* <Route path="/warehouse/warehouses/:warehouseId" element={<WarehousePage />} /> */}
            <Route path="/warehouse/locations" element={<LocationsPage />} />
            <Route path="/warehouse/areas" element={<AreasPage />} />
            <Route path="/warehouse/asrs-servers" element={<AsrsServersPage />} />
            <Route path="/warehouse/asrs-servers/:serverId" element={<AsrsServerPage />} />
          </Routes>
        </PageContainer>
      </DashboardLayout>
    </AppProvider>
  );
}
